'use client';

import { useRouter } from 'next/navigation';
import { type FC, useEffect, useRef, useState } from 'react';

import Icon from 'molecules/Icon';
import Link from 'molecules/Link';

import NavDropdown from 'components/Header/NavDropdown';
import styles from 'components/Header/NavMenu/nav-main.module.scss';
import SearchDropdown from 'components/SearchDropdown';

import { useSearchAutocomplete } from 'utils/hooks/useSearchAutocomplete';

import type { GlobalHeaderProps } from 'components/Header/headerTypes';
import type { FormEvent } from 'react';

type NavMenuProps = GlobalHeaderProps['data'];

const NavMain: FC<NavMenuProps> = ({ menus }) => {
  const dropdownRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const router = useRouter();
  const { results, loading, inputValue, setInputValue, clearResults, isOpen } = useSearchAutocomplete();

  const setOpen = (index: number): void => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const toggleSearch = () => {
    setSearchOpen(prev => !prev);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    clearResults();
  };

  const handleClick = () => {
    setOpenIndex(null);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenIndex(null);
        closeSearch();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenIndex(null);
      }

      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        closeSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const search = formData.get('search');

    if (search) {
      const searchValue = search;
      router.push(`/search?q=${searchValue}`);
      closeSearch();
    }
  };

  return (
    <div className={styles.navContainer}>
      <nav aria-label="Main menu">
        <ul className={styles.menuItems}>
          {menus?.map(({ label, url = '', menuItems }, index) => (
            <li key={label} className={styles.listItem}>
              {menuItems?.length === 0 ? (
                <Link className={styles.menuItem} href={url}>
                  {label}
                </Link>
              ) : (
                <>
                  <button
                    className={styles.menuItem}
                    aria-expanded={index === openIndex}
                    onClick={() => setOpen(index)}
                    data-testid={`menuItem--${label?.toLowerCase()}`}
                  >
                    {label}
                  </button>
                  {menuItems && (
                    <NavDropdown
                      ref={dropdownRef}
                      expanded={index === openIndex}
                      handleClick={handleClick}
                      items={menuItems}
                    />
                  )}
                </>
              )}
            </li>
          ))}
          <li>
            <div className={styles.listItem}>
              <button
                onClick={toggleSearch}
                className={styles.menuItem}
                aria-expanded={searchOpen}
                aria-controls="search-container"
              >
                <Icon size={21} className={styles.searchIcon} icon="icon-search" />
              </button>
            </div>
          </li>
        </ul>
        {searchOpen && (
          <div ref={searchRef} id="search-container" className={styles.searchContainer}>
            <form onSubmit={handleSearch} className={styles.searchForm}>
              <div className={styles.iconContainer}>
                <Icon size={19} className={styles.searchIcon} icon="icon-search" />
              </div>
              <input
                name="search"
                type="text"
                className={styles.searchInput}
                placeholder="Search"
                aria-label="Search"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
              />
            </form>
            <SearchDropdown
              key={inputValue}
              results={results}
              loading={loading}
              inputValue={inputValue}
              isOpen={isOpen}
              variant="nav"
            />
          </div>
        )}
      </nav>
    </div>
  );
};

export default NavMain;
